// ============================================================
// VCS.ChatContacts — Kontaktverwaltung
// - Kontaktliste laden, rendern, filtern (Suche)
// - Kontaktanfragen senden, annehmen, ablehnen
// - Kontakt-Detail-View (Name, Status, Telefon)
// - Anruf starten aus Kontaktliste
// - Kontakt blockieren / loeschen
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatContacts = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._view = {};
        // Lock gegen parallele Einloese-Versuche: 'chat-authed' kommt bei jedem
        // Reconnect, und ein liegengebliebener Token wird dann erneut probiert.
        this._invitePending = false;
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async loadContacts() {
        if (!VCS.state.chatMyPhone) {
            return;
        }
        const list = $('#contactsList')[0];
        if (!list) {
            return;
        }

        try {
            const reqRes = await fetch(VCS.Chat.API_BASE + '/contacts/requests', {
                headers: VCS.chat?.authHeaders()
            });
            if (reqRes.ok) {
                const reqData = await reqRes.json();
                VCS.state.chatPendingRequests = reqData.incoming || [];
                VCS.state.chatSentRequests = reqData.sent || [];
            }
        } catch (e) {
            console.error('[chat] load contact requests error:', e);
        }

        this.updateContactRequestBadge();

        let serverContacts = [];
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts?phone=' + encodeURIComponent(VCS.state.chatMyPhone), {
                headers: VCS.chat?.authHeaders()
            });
            if (res.ok) {
                serverContacts = await res.json();
            }
        } catch (e) {
            console.error('[chat] load contacts error:', e);
        }

        VCS.state.chatApprovedContacts = new Set(serverContacts.map(c => c.phoneNumber));
        const wasFirstLoad = !VCS.state.chatContactsLoaded;
        VCS.state.chatContactsLoaded = true;
        // Beim ersten Laden Chat-Liste nachrendern, damit der Filter greift
        if (wasFirstLoad) {
            VCS.chatMessages?.renderChatList?.();
        }

        const merged = new Map();
        VCS.state.chatSavedContacts.forEach(c => merged.set(c.phoneNumber, {
            phoneNumber: c.phoneNumber,
            displayName: c.displayName || c.phoneNumber
        }));
        serverContacts.forEach(c => {
            // Dating-Match: die Nummer darf nirgends auftauchen, also auch nicht als
            // Namens-Fallback. Der Server liefert dort schon den Dating-Profilnamen;
            // bleibt der leer, steht der Match-Hinweis statt der Nummer.
            const fallbackName = c.isDatingMatch ? t('datingContactMatch') : c.phoneNumber;
            const entry = merged.get(c.phoneNumber);
            if (entry) {
                entry.displayName = c.displayName || fallbackName;
                entry.avatarHash = c.avatarHash || null;
                entry.isDatingMatch = !!c.isDatingMatch;
            } else {
                merged.set(c.phoneNumber, {
                    phoneNumber: c.phoneNumber,
                    displayName: c.displayName || fallbackName,
                    avatarHash: c.avatarHash || null,
                    isDatingMatch: !!c.isDatingMatch
                });
            }
        });

        let localUpdated = false;
        serverContacts.forEach(sc => {
            const local = VCS.state.chatSavedContacts.find(lc => lc.phoneNumber === sc.phoneNumber);
            if (local && sc.displayName && local.displayName !== sc.displayName) {
                local.displayName = sc.displayName;
                localUpdated = true;
            }
        });
        if (localUpdated) {
            localStorage.setItem('chat-contacts', JSON.stringify(VCS.state.chatSavedContacts));
        }

        merged.delete(VCS.state.chatMyPhone);

        const contacts = [...merged.values()].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
        VCS.state.chatMergedContacts = contacts;

        let html = '';

        if (VCS.state.chatPendingRequests.length > 0) {
            html += `<div class="contact-request-section">`;
            html += `<div class="contact-request-section-title">${t('pendingRequests')}</div>`;
            html += VCS.state.chatPendingRequests.map(r => {
                const initial = (r.displayName || r.fromPhone || '?')[0].toUpperCase();
                const name = r.displayName || r.fromPhone;
                return `<div class="chat-list-item d-flex align-items-center gap-3">
                <div class="chat-list-avatar">${initial}</div>
                <div class="flex-grow-1 min-w-0">
                    <div class="chat-list-name">${name}</div>
                    <div class="chat-list-preview">${r.fromPhone}</div>
                    <div class="contact-request-actions">
                        <button class="contact-req-btn contact-req-btn--accept" data-request-id="${r.id}" data-action="accept">${t('contactAccept')}</button>
                        <button class="contact-req-btn contact-req-btn--decline" data-request-id="${r.id}" data-action="decline">${t('contactDecline')}</button>
                    </div>
                </div>
            </div>`;
            }).join('');
            html += `</div>`;
        }

        if (VCS.state.chatSentRequests.length > 0) {
            html += `<div class="contact-request-section">`;
            html += `<div class="contact-request-section-title">${t('sentRequests')}</div>`;
            html += VCS.state.chatSentRequests.map(r => {
                const initial = (r.toPhone || '?')[0].toUpperCase();
                const serverContact = serverContacts.find(c => c.phoneNumber === r.toPhone);
                const name = serverContact?.displayName || r.toPhone;
                return `<div class="chat-list-item d-flex align-items-center gap-3">
                <div class="chat-list-avatar">${initial}</div>
                <div class="flex-grow-1 min-w-0">
                    <div class="chat-list-name">${name}</div>
                    <div class="chat-list-preview">${r.toPhone}</div>
                    <div class="contact-request-actions">
                        <button class="contact-req-btn contact-req-btn--decline" data-request-id="${r.id}" data-action="cancel">${t('contactRequestCancel')}</button>
                    </div>
                </div>
            </div>`;
            }).join('');
            html += `</div>`;
        }

        if (!contacts.length && !VCS.state.chatPendingRequests.length && !VCS.state.chatSentRequests.length) {
            list.innerHTML = `<div class="chat-list-empty"><span>${t('contactsEmpty')}</span></div>`;
            return;
        }

        html += contacts.map(c => {
            const initial = (c.displayName || c.phoneNumber || '?')[0].toUpperCase();
            const online = VCS.state.chatPresenceMap[c.phoneNumber]?.online;
            const activity = VCS.state.chatActivityMap[c.phoneNumber];
            const name = c.displayName || c.phoneNumber;
            const eName = escapeHtml(name);
            const ePhone = escapeHtml(c.phoneNumber);

            const contactChat = VCS.state.chatChats.find(ch => VCS.chat?.peerPhone?.(ch) === c.phoneNumber);
            const e2e = contactChat ? !!VCS.state.chatE2EEnabled[contactChat.id] : false;
            const e2eBadge = e2e
                ? `<span class="chat-list-e2e-badge chat-list-e2e-badge--locked" data-tooltip-i18n="e2eEnabled"><svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>`
                : `<span class="chat-list-e2e-badge chat-list-e2e-badge--unlocked" data-tooltip-i18n="e2eDisabled"><svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 8.7-2.7"/></svg></span>`;

            let subtitleHtml;
            if (activity?.type === 'recording') {
                subtitleHtml = `<span class="contact-activity contact-activity--recording"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" style="vertical-align:-1px"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg> ${t('chatRecording')}</span>`;
            } else if (activity?.type === 'typing') {
                subtitleHtml = `<span class="contact-activity contact-activity--typing">${t('chatTyping')}</span>`;
            } else if (online) {
                subtitleHtml = `<span class="contact-activity contact-activity--online">${t('chatOnline')}</span>`;
            } else if (c.isDatingMatch) {
                // Aus einem Dating-Match entstandener Kontakt: statt der Nummer der
                // Match-Hinweis. Die beiden haben die Nummer nie ausgetauscht -- der
                // Kontakt entsteht serverseitig aus dem Swipe.
                subtitleHtml = `<span class="contact-activity contact-activity--match"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" style="vertical-align:-1px"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg> ${t('datingContactMatch')}</span>`;
            } else {
                subtitleHtml = ePhone;
            }

            const av = VCS.chat?.avatarHtml?.(initial, c.avatarHash) || { html: initial, hasAvatar: false };
            return `<div class="chat-list-item d-flex align-items-center gap-3" data-phone="${ePhone}" data-name="${eName}">
            <div class="chat-list-avatar${av.hasAvatar ? ' has-avatar' : ''}">${av.html}${online ? '<span class="chat-list-online-dot"></span>' : ''}${e2eBadge}</div>
            <div class="flex-grow-1 min-w-0">
                <div class="chat-list-name">${eName}</div>
                <div class="chat-list-preview">${subtitleHtml}</div>
            </div>
            <div class="contact-action-btns d-flex gap-2">
                <button class="contact-action-btn contact-action-btn--chat" data-action="chat" title="Chat">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                </button>
                <button class="contact-action-btn contact-action-btn--audio" data-action="audio" title="Anruf">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </button>
                <button class="contact-action-btn contact-action-btn--video" data-action="video" title="Video">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="7" width="13" height="10" rx="2"></rect><path d="M16 10l5-3v10l-5-3z"></path></svg>
                </button>
            </div>
        </div>`;
        }).join('');
        list.innerHTML = html;

        list.querySelectorAll('.contact-action-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const item = btn.closest('.chat-list-item');
                if (!item) {
                    return;
                }
                const phone = item.dataset.phone;
                const name = item.dataset.name;
                const action = btn.dataset.action;
                if (action === 'chat') {
                    if (VCS.state.chatApprovedContacts.has(phone)) {
                        this.startChatWith(phone, name);
                    } else {
                        const iconSvg = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>';
                        VCS.ui?.showConfirm?.(t('confirmContactRequest').replace('{0}', name), iconSvg).then(ok => {
                            if (ok) {
                                this.startChatWith(phone, name);
                            }
                        });
                    }
                } else if (action === 'audio') {
                    const audioIcon = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>';
                    VCS.ui?.showConfirm?.(t('confirmStartAudioCall').replace('{0}', name), audioIcon).then(ok => {
                        if (ok) this.callContact(phone, name, 'audio');
                    });
                } else if (action === 'video') {
                    const videoIcon = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="7" width="13" height="10" rx="2"></rect><path d="M16 10l5-3v10l-5-3z"></path></svg>';
                    VCS.ui?.showConfirm?.(t('confirmStartVideoCall').replace('{0}', name), videoIcon).then(ok => {
                        if (ok) this.callContact(phone, name, 'video');
                    });
                }
            });
        });

        // Context-Menu: Rechtsklick (Desktop) + Long-Press (Touch) je Kontakt
        const LONG_PRESS_MS = 450;
        list.querySelectorAll('.chat-list-item[data-phone]').forEach(item => {
            const phone = item.dataset.phone;
            const name = item.dataset.name;
            if (!phone || !VCS.state.chatApprovedContacts.has(phone)) {
                // Nur freigegebene Kontakte koennen entfernt werden (Pending/Sent haben eigene Buttons)
                return;
            }

            item.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                this.showContactContextMenu(e.clientX, e.clientY, phone, name);
            });

            let pressTimer = null;
            let pressX = 0, pressY = 0;
            item.addEventListener('touchstart', (e) => {
                if (e.target.closest('.contact-action-btn, .chat-list-avatar.has-avatar')) {
                    return;
                }
                const t = e.touches[0];
                pressX = t.clientX;
                pressY = t.clientY;
                pressTimer = setTimeout(() => {
                    pressTimer = null;
                    this.showContactContextMenu(pressX, pressY, phone, name);
                }, LONG_PRESS_MS);
            }, { passive: true });
            const cancelPress = () => { if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; } };
            item.addEventListener('touchend',    cancelPress);
            item.addEventListener('touchcancel', cancelPress);
            item.addEventListener('touchmove', (e) => {
                if (!pressTimer) return;
                const t = e.touches[0];
                if (Math.abs(t.clientX - pressX) > 10 || Math.abs(t.clientY - pressY) > 10) {
                    cancelPress();
                }
            }, { passive: true });
        });

        list.querySelectorAll('.chat-list-avatar.has-avatar').forEach(avatar => {
            avatar.style.cursor = 'pointer';
            avatar.addEventListener('click', (e) => {
                e.stopPropagation();
                const item = avatar.closest('.chat-list-item');
                if (!item) return;
                const name = item.dataset.name;
                const img = avatar.querySelector('.chat-list-avatar-img');
                if (img?.src) {
                    const url = new URL(img.src);
                    const hash = url.pathname.split('/').pop();
                    VCS.chat?.openAvatarViewer?.(name, hash);
                }
            });
        });

        list.querySelectorAll('.contact-req-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const requestId = btn.dataset.requestId;
                if (btn.dataset.action === 'accept') {
                    this.acceptRequest(requestId);
                } else if (btn.dataset.action === 'decline') {
                    this.declineRequest(requestId);
                } else if (btn.dataset.action === 'cancel') {
                    this.cancelRequest(requestId);
                }
            });
        });
    }

    saveContact(phone, displayName) {
        if (!phone) {
            return;
        }
        const existing = VCS.state.chatSavedContacts.find(c => c.phoneNumber === phone);
        if (existing) {
            if (displayName && displayName !== phone) {
                existing.displayName = displayName;
            }
        } else {
            VCS.state.chatSavedContacts.push({
                phoneNumber: phone,
                displayName: displayName || phone
            });
        }
        localStorage.setItem('chat-contacts', JSON.stringify(VCS.state.chatSavedContacts));
    }

    async acceptRequest(requestId) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/accept', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({requestId: parseInt(requestId)})
            });
            if (res.ok) {
                const data = await res.json();
                VCS.ui?.showToast?.(t('contactAccepted'), 'success');
                if (data.chatId) {
                    await VCS.chatSetup?.loadChats?.();
                    VCS.chatSetup?.openConversation?.(data.chatId);
                }
                this.loadContacts();
            }
        } catch (e) {
            console.error('[chat] accept contact request error:', e);
        }
    }

    async declineRequest(requestId) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/decline', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({requestId: parseInt(requestId)})
            });
            if (res.ok) {
                VCS.ui?.showToast?.(t('contactDeclined'), 'info');
                this.loadContacts();
            }
        } catch (e) {
            console.error('[chat] decline contact request error:', e);
        }
    }

    async cancelRequest(requestId) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/cancel', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({requestId: parseInt(requestId)})
            });
            if (res.ok) {
                VCS.ui?.showToast?.(t('contactRequestCancelled'), 'info');
                this.loadContacts();
            }
        } catch (e) {
            console.error('[chat] cancel contact request error:', e);
        }
    }

    async loadPendingCount() {
        if (!VCS.state.chatMyPhone) {
            return;
        }
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/requests', {
                headers: VCS.chat?.authHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                VCS.state.chatPendingRequests = data.incoming || [];
                VCS.state.chatSentRequests = data.sent || [];
                this.updateContactRequestBadge();
            }
        } catch (e) {
            console.error('[chat] load pending request count error:', e);
        }
    }

    async startChatWith(phone, displayName) {
        if (!VCS.state.chatMyPhone) {
            return;
        }
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/request', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    toPhone    : phone,
                    displayName: displayName || phone
                })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                if (err.error === 'user_not_found') {
                    VCS.ui?.showToast?.(t('contactUserNotFound'), 'error', 5000);
                } else {
                    VCS.ui?.showToast?.('Error', 'error');
                }
                return;
            }
            const result = await res.json();
            if (result.status === 'already_approved' || result.status === 'auto_accepted') {
                if (result.status === 'auto_accepted') {
                    VCS.ui?.showToast?.(t('contactRequestAutoAccepted'), 'success');
                }
                const chatRes = await fetch(VCS.Chat.API_BASE + '/chats/start', {
                    method : 'POST',
                    headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                    body   : JSON.stringify({participantPhone: phone})
                });
                if (chatRes.ok) {
                    const chat = await chatRes.json();
                    const peerName = displayName || phone;
                    chat._peerName = peerName;
                    this.saveContact(phone, peerName);
                    if (!VCS.state.chatChats.some(c => c.id === chat.id)) {
                        VCS.state.chatChats.push(chat);
                    }
                    VCS.nav?.switchTab?.('tabChats');
                    VCS.chatSetup?.openConversation?.(chat.id, phone, peerName);
                }
            } else if (result.status === 'already_pending') {
                VCS.ui?.showToast?.(t('contactAlreadyPending'), 'info');
            } else if (result.status === 'pending') {
                VCS.ui?.showToast?.(t('contactRequestSent'), 'success');
                this.loadContacts();
            }
        } catch (e) {
            console.error('[chat] contact request error:', e);
        }
    }

    async callContact(phone, displayName, callType) {
        const s = VCS.state;
        if (!s.chatMyPhone) {
            return;
        }

        const callerName = ($('#regNameInput')[0]?.value || '').trim() || s.myName || s.chatMyName;

        // Gespraechszusammenfassung: bewusst hier, VOR dem Waehlen. Beim
        // Gegenueber klingelt noch nichts, niemand wartet auf die Antwort.
        // Der Wunsch geht erst nach dem Registrieren an den Server
        // (signaling.js, case 'registered').
        VCS.callControls?.resetSummary?.();
        if (this._maySummarize(phone)) {
            const wish = await VCS.ui?.showConfirm?.(
                t('callSummaryAsk'), this._summaryIcon(), t('callSummaryYes'), t('callSummaryNo'));
            s.callSummaryWish = wish === true;
        }

        let callId;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/call/direct', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    callerPhone: VCS.state.chatMyPhone,
                    calleePhone: phone,
                    callerName : callerName,
                    callType   : callType
                })
            });
            if (!res.ok) {
                VCS.ui?.showToast?.(t('callFailed'), 'error');
                return;
            }
            const data = await res.json();
            callId = data.callId;
            if (!callId) {
                VCS.ui?.showToast?.(t('callFailed'), 'error');
                return;
            }
        } catch (e) {
            console.error('[call] direct call error:', e);
            VCS.ui?.showToast?.(t('callFailed'), 'error');
            return;
        }

        VCS.session?.updateRoomId(callId);
        s.myName = callerName;
        s.directCallActive = true;
        s.directCallPeerName = displayName || phone;
        s.directCallPeerPhone = phone;
        if (callType === 'audio') {
            s.videoOff = true;
        }

        if (!s.localStream) {
            await VCS.media?.initPreview?.();
        }

        VCS.session?.update({
            name : s.myName,
            role : null,
            phase: 'waiting'
        });
        VCS.nav?.showScreen?.('waitingScreen');
        $('#hostWaiting')[0].classList.remove('hidden');
        $('#guestWaiting')[0].classList.add('hidden');
        VCS.session?.setRejoinHint(false);

        const titleEl = $('#hostWaiting')[0]?.querySelector('.waiting-title');
        if (titleEl) {
            titleEl.textContent = t('callingPeer').replace('{name}', s.directCallPeerName);
        }
        const subEl = $('#hostWaiting')[0]?.querySelector('.waiting-subtitle');
        if (subEl) {
            subEl.textContent = '';
        }

        VCS.signaling?.requestRegister();
    }

    // -- Minuten teilen (2026-08-31) ------------------------------------
    // Festlegung 29.08.: Freigabe pro Kontakt, Abzug beim Teiler. Sichtbarkeit
    // des Menue-Buttons (kein Bot/Gruppe/Dating/Free) regelt chat-setup.js
    // openConversation -- diese beiden Methoden sind der eigentliche
    // Ein-/Ausschalt-Vorgang, aufgerufen aus chat-messages.js (Menue-Klick)
    // und chat-setup.js (Beenden-Knopf in den Einstellungen).

    async toggleShareMinutes(phone, name) {
        let shares = [];
        let maxBeneficiaries = 3;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/minutes/shares', {
                headers: VCS.chat?.authHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                shares = data.shares || [];
                maxBeneficiaries = data.maxBeneficiaries || maxBeneficiaries;
            }
        } catch (e) {
            console.error('[chat] Minuten-Teilen-Status nicht ladbar:', e);
        }
        if (shares.some(s => s.beneficiaryPhone === phone)) {
            await this.stopSharingMinutes(phone, name);
            return;
        }
        if (shares.length >= maxBeneficiaries) {
            VCS.ui?.showToast?.(t('liveShareLimitReached').replace('{max}', String(maxBeneficiaries)), 'warning', 6000);
            return;
        }
        // Pflicht-Hinweis mit der Bedingung, unter der sich der Verbrauch
        // verdoppelt -- muss bestaetigt werden, keine Fussnote (Plan Abschnitt 5).
        const message = t('liveShareToggleLabel').replace('{name}', name) + ' ' +
            t('liveShareConsentText').replace(/\{name\}/g, name);
        // Eigener Button-Text noetig: der Default von showConfirm ist "Starten"
        // (fuer die Anruf-Dialoge gedacht) und liest sich hier wie "Call starten".
        const ok = await VCS.ui?.showConfirm?.(message, null, t('liveShareStartConfirmButton'));
        if (!ok) return;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/minutes/share', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({beneficiaryPhone: phone})
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data.error || 'unknown');
            $('#chatShareMinutesBtn')[0]?.classList.add('chat-header-menu-item--active');
            VCS.ui?.showToast?.(t('liveShareEnabledToast').replace('{name}', name), 'success', 6000);
            VCS.chatSetup?._loadLiveShares?.();
        } catch (e) {
            console.error('[chat] share enable error:', e);
            VCS.ui?.showToast?.(t('liveShareError'), 'error', 6000);
        }
    }

    async stopSharingMinutes(phone, name) {
        const ok = await VCS.ui?.showConfirm?.(t('liveShareStopConfirm').replace('{name}', name), null, t('liveShareStopConfirmButton'));
        if (!ok) return;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/minutes/share', {
                method : 'DELETE',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({beneficiaryPhone: phone})
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data.error || 'unknown');
            $('#chatShareMinutesBtn')[0]?.classList.remove('chat-header-menu-item--active');
            VCS.ui?.showToast?.(t('liveShareDisabledToast').replace('{name}', name), 'info', 6000);
            VCS.chatSetup?._loadLiveShares?.();
        } catch (e) {
            console.error('[chat] share disable error:', e);
            VCS.ui?.showToast?.(t('liveShareError'), 'error', 6000);
        }
    }

    // Gespraechszusammenfassung (2026-09-08): Darf der Dialog fuer diesen
    // Kontakt kommen? Zwei Bedingungen. Das Recht kommt vom Server
    // (callSummaryAvailable, inkl. hinterlegter Mailadresse). Dazu darf der
    // Chat nicht verschluesselt sein: dort laeuft keine Live-Uebersetzung,
    // es entstuende also kein Transkript zum Zusammenfassen. Lieber nicht
    // fragen als eine Mail versprechen, die nie kommt.
    _maySummarize(phone) {
        if (!VCS.state.callSummaryAvailable) {
            return false;
        }
        const chat = VCS.state.chatChats.find(ch => VCS.chat?.peerPhone?.(ch) === phone);
        return chat ? !VCS.state.chatE2EEnabled[chat.id] : false;
    }

    // Briefumschlag, in beiden Dialogen dasselbe Zeichen. Groesse als Attribut:
    // .confirm-icon setzt nur die Farbe, ohne Masse zoege das SVG dort auf die
    // volle Kartenbreite. Im Anruf-Overlay ueberschreibt das SCSS auf 16px.
    _summaryIcon() {
        return '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/></svg>';
    }

    // Ankreuzfeld "Gespraech zusammenfassen" im Anruf-Overlay.
    _summaryOptInHtml() {
        return `
            <button class="incoming-call-optin" data-action="toggle-summary" aria-pressed="false">
                <span class="incoming-call-optin-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2"
                         stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 7"/></svg>
                </span>
                <span class="incoming-call-optin-text">
                    <span class="incoming-call-optin-title">${t('callSummaryOptIn')}</span>
                    <span class="incoming-call-optin-hint">${t('callSummaryOptInHint')}</span>
                </span>
            </button>`;
    }

    // Ankreuzfeld: aendert nur den Haken, nimmt den Anruf NICHT an. Der
    // Anruf wird weiterhin ueber den gruenen Knopf angenommen -- zwei
    // Entscheidungen, zwei Bedienschritte. Der Zustand steht in aria-pressed,
    // der Annehmen-Knopf liest ihn von dort.
    _bindSummaryOptIn(overlay) {
        const optIn = overlay.querySelector('[data-action="toggle-summary"]');
        if (!optIn) {
            return;
        }
        optIn.onclick = () => {
            const on = optIn.getAttribute('aria-pressed') !== 'true';
            optIn.classList.toggle('incoming-call-optin--on', on);
            optIn.setAttribute('aria-pressed', on ? 'true' : 'false');
        };
    }

    // Kaltstart ueber Push: Das Overlay steht, bevor chat-authed (Recht) und
    // /chats (E2E-Status) angekommen sind -- _maySummarize() ist dann noch
    // false und das Ankreuzfeld fehlt. Wird nach beiden Antworten gerufen und
    // reicht das Feld nach, solange das Overlay noch offen ist.
    refreshIncomingCallOptIn() {
        const overlay = document.querySelector('.incoming-call-overlay');
        if (!overlay || overlay.querySelector('[data-action="toggle-summary"]')) {
            return;
        }
        if (!this._maySummarize(overlay.dataset.callerPhone || '')) {
            return;
        }
        overlay.querySelector('.incoming-call-actions')?.insertAdjacentHTML('beforebegin', this._summaryOptInHtml());
        this._bindSummaryOptIn(overlay);
        console.log('[chat] incoming call: summary opt-in added after late auth/chats');
    }

    showIncomingCallToast(msg) {
        const callerName = msg.callerName || msg.callerPhone || 'Unbekannt';
        const callType = msg.callType || 'video';
        const callId = msg.callId;
        const isVideo = callType === 'video';
        const initial = callerName.charAt(0);
        // Dritter Knopf statt eines zweiten Schrittes nach dem Annehmen: der
        // Anrufer wartet bereits, jede Sekunde zaehlt hier.
        const maySummarize = this._maySummarize(msg.callerPhone || '');

        document.querySelector('.incoming-call-overlay')?.remove();

        const overlay = document.createElement('div');
        overlay.className = 'incoming-call-overlay';
        overlay.dataset.callerPhone = msg.callerPhone || '';
        overlay.innerHTML = `
            <div class="incoming-call-avatar">${escapeHtml(initial)}</div>
            <div class="incoming-call-name">${escapeHtml(callerName)}</div>
            <div class="incoming-call-type">${isVideo ? t('videoCall') : t('phoneCall')}</div>
            ${maySummarize ? this._summaryOptInHtml() : ''}
            <div class="incoming-call-actions">
                <button class="incoming-call-btn incoming-call-btn--decline" data-action="decline">
                    <span class="incoming-call-btn-circle">
                        <svg viewBox="0 0 24 24"><path d="M6.34 17.66l-1.41-1.41L10.59 12 4.93 6.34l1.41-1.41L12 10.59l5.66-5.66 1.41 1.41L13.41 12l5.66 5.66-1.41 1.41L12 13.41z"/></svg>
                    </span>
                    <span class="incoming-call-btn-label">${t('decline')}</span>
                </button>
                <button class="incoming-call-btn incoming-call-btn--accept" data-action="accept">
                    <span class="incoming-call-btn-circle">
                        <svg viewBox="0 0 24 24"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 01.98-.27c1.1.35 2.3.53 3.53.53a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.23.18 2.43.53 3.53a1 1 0 01-.27.97z"/></svg>
                    </span>
                    <span class="incoming-call-btn-label">${t('accept')}</span>
                </button>
            </div>
        `;

        const acceptCall = (withSummary) => {
            overlay.remove();
            const s = VCS.state;
            // Zustand des vorherigen Calls loswerden, bevor der neue startet.
            VCS.callControls?.resetSummary?.();
            s.callSummaryWish = !!withSummary;
            VCS.session?.updateRoomId(callId);
            s.myName = ($('#regNameInput')[0]?.value || '').trim() || s.myName || s.chatMyName;
            s.directCallActive = true;
            s.directCallPeerName = callerName;
            s.directCallPeerPhone = msg.callerPhone || '';
            if (callType === 'audio') {
                s.videoOff = true;
            }
            const joinCall = () => {
                VCS.session?.update({name: s.myName, role: null, phase: 'waiting'});
                VCS.nav?.showScreen?.('waitingScreen');
                $('#guestWaiting')[0]?.classList.remove('hidden');
                $('#hostWaiting')[0]?.classList.add('hidden');
                VCS.session?.setRejoinHint(false);
                VCS.signaling?.requestRegister();
            };
            if (!s.localStream) {
                VCS.media?.initPreview?.().then(joinCall);
            } else {
                joinCall();
            }
        };

        this._bindSummaryOptIn(overlay);

        // Zustand beim Klick lesen, nicht beim Aufbau: das Ankreuzfeld kann
        // nach dem Kaltstart erst spaeter dazukommen (refreshIncomingCallOptIn).
        overlay.querySelector('[data-action="accept"]').onclick = () => {
            const optIn = overlay.querySelector('[data-action="toggle-summary"]');
            acceptCall(optIn?.getAttribute('aria-pressed') === 'true');
        };
        overlay.querySelector('[data-action="decline"]').onclick = () => {
            overlay.remove();
            // Server über Ablehnung informieren
            fetch(VCS.Chat.API_BASE + '/call/direct/decline', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({ callId }),
            }).catch(() => {});
        };

        document.body.appendChild(overlay);
        setTimeout(() => {
            if (overlay.parentElement) {
                overlay.remove();
            }
        }, 30000);
    }

    async startNewChat() {
        const phoneInput = $('#newChatPhoneInput')[0];
        const raw = phoneInput?.value.trim() || '';
        const phone = VCS.chat?.normalizePhone?.(raw);
        if (!phone) {
            if (raw) {
                VCS.ui?.showToast?.(t('phoneInvalid'), 'error', 5000);
            }
            return;
        }

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/request', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({toPhone: phone})
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                if (err.error === 'user_not_found') {
                    VCS.ui?.showToast?.(t('contactUserNotFound'), 'error', 5000);
                } else {
                    VCS.ui?.showToast?.(err.error || 'Error', 'error');
                }
                return;
            }

            const result = await res.json();

            const modal = bootstrap.Modal.getInstance($('#newChatModal')[0]);
            if (modal) {
                modal.hide();
            }
            if (phoneInput) {
                phoneInput.value = '';
            }

            if (result.status === 'already_approved' || result.status === 'auto_accepted') {
                if (result.status === 'auto_accepted') {
                    VCS.ui?.showToast?.(t('contactRequestAutoAccepted'), 'success');
                }
                const chatRes = await fetch(VCS.Chat.API_BASE + '/chats/start', {
                    method : 'POST',
                    headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                    body   : JSON.stringify({participantPhone: phone})
                });
                if (chatRes.ok) {
                    const chat = await chatRes.json();
                    const syncRes = await fetch(VCS.Chat.API_BASE + '/contacts/sync', {
                        method : 'POST',
                        headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                        body   : JSON.stringify({phones: [VCS.chat?.peerPhone?.(chat)]})
                    });
                    if (syncRes.ok) {
                        const data = await syncRes.json();
                        const reg = (data.registered || []).find(r => r.phone === VCS.chat?.peerPhone?.(chat));
                        chat._peerName = reg ? reg.displayName : VCS.chat?.peerPhone?.(chat);
                    } else {
                        chat._peerName = VCS.chat?.peerPhone?.(chat);
                    }
                    this.saveContact(VCS.chat?.peerPhone?.(chat), chat._peerName);
                    if (!VCS.state.chatChats.some(c => c.id === chat.id)) {
                        VCS.state.chatChats.push(chat);
                    }
                    VCS.chatSetup?.openConversation?.(chat.id);
                }
            } else if (result.status === 'already_pending') {
                VCS.ui?.showToast?.(t('contactAlreadyPending'), 'info');
            } else if (result.status === 'pending') {
                VCS.ui?.showToast?.(t('contactRequestSent'), 'success');
                this.loadContacts();
            }
        } catch (e) {
            console.error('[chat] startNewChat error:', e);
            VCS.ui?.showToast?.('Network error', 'error');
        }
    }

    // Einladungslink erzeugen und teilen. Gedacht fuer alle, die noch kein
    // Bernoria-Konto haben -- deren Nummer steht im Verzeichnis nicht, ueber
    // /contacts/request kaeme man also nie an sie heran.
    // Der Link ist einmalig nutzbar und laeuft nach 7 Tagen ab (Server-Default).
    async createInviteLink() {
        const btn = $('#btnCreateInvite')[0];
        if (btn?.disabled) {
            return;
        }
        if (btn) {
            btn.disabled = true;
        }

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/invite/create', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({kind: 'contact'})
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error('[invite] create failed status=' + res.status + ' error=' + err.error);
                if (err.error === 'too_many_active_invites') {
                    VCS.ui?.showToast?.(t('inviteTooMany'), 'error', 5000);
                } else {
                    VCS.ui?.showToast?.(t('inviteCreateFailed'), 'error', 5000);
                }
                return;
            }

            const data = await res.json();
            if (!data.url) {
                console.error('[invite] create response without url');
                VCS.ui?.showToast?.(t('inviteCreateFailed'), 'error', 5000);
                return;
            }

            console.log('[invite] created token=' + String(data.token).slice(0, 6) + ' maxUses=' + data.maxUses);

            // Erst das Modal schliessen, dann das Versand-Sheet zeigen -- sonst
            // liegen zwei Overlays uebereinander.
            const modal = bootstrap.Modal.getInstance($('#newChatModal')[0]);
            if (modal) {
                modal.hide();
            }
            this._showInviteSheet(data.url, data.token);
        } catch (e) {
            console.error('[invite] create error:', e);
            VCS.ui?.showToast?.(t('inviteCreateFailed'), 'error', 5000);
        } finally {
            if (btn) {
                btn.disabled = false;
            }
        }
    }

    // Versand-Auswahl fuer einen fertigen Einladungslink.
    //
    // Warum ein eigenes Sheet und nicht direkt navigator.share():
    //   1. Die Web Share API verlangt eine frische Nutzer-Interaktion. Zwischen
    //      dem Klick und dem Teilen liegt aber der fetch auf /invite/create --
    //      danach ist die Aktivierung weg und share() wirft NotAllowedError.
    //      Ein Klick IM Sheet ist wieder frisch.
    //   2. Auf dem Desktop gibt es navigator.share meist gar nicht. Ohne eigene
    //      Kanaele bliebe dort nur "in die Zwischenablage kopiert" -- und genau
    //      das ist kein Verschicken.
    //
    // options (Gast-Anruf, siehe guest-invite.js): title, shareText, shareTitle
    // und note ersetzen die Texte der Kontakt-Einladung. Fehlen sie, bleibt
    // alles wie bisher.
    _showInviteSheet(url, token, options = {}) {
        document.querySelector('.invite-sheet-overlay')?.remove();

        const text = (options.shareText || t('inviteShareText')).replace('{url}', url);

        // Doppelt kodiert wird hier nichts: url steckt bereits in text.
        const encText = encodeURIComponent(text);

        // E-Mail und SMS laufen ueber den Server, nicht ueber mailto:/sms:.
        // Der Server hat fuer beides eine fertige Zustellung -- die Mail mit
        // gebrandetem Template, die SMS ueber seven.io. Damit kommt eine echte
        // Einladung an statt eines vorausgefuellten Entwurfs, der einen
        // eingerichteten Mail-Client oder ein Handy voraussetzt.
        //
        // Beide klappen deshalb ein Eingabefeld auf: der Server braucht die
        // Zieladresse. WhatsApp bleibt ein normaler Link.
        const targets = [
            {
                key : 'whatsapp',
                href: 'https://wa.me/?text=' + encText,
                mode: 'ext',
                icon: '<path d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.1-.6.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5 0-.2-.6-1.5-.9-2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.5-.3z"/><path d="M12 2a10 10 0 0 0-8.6 15L2 22l5.2-1.4A10 10 0 1 0 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.1.8.8-3-.2-.3A8.2 8.2 0 1 1 12 20.2z"/>'
            },
            {
                // Offizieller Teilen-Link von Telegram: oeffnet die App (Handy)
                // bzw. Telegram Web/Desktop mit fertiger Nachricht. Den Link
                // setzt Telegram selbst vor den Text -- deshalb hier ohne {url},
                // sonst stuende er doppelt in der Nachricht.
                key : 'telegram',
                href: 'https://t.me/share/url?url=' + encodeURIComponent(url)
                    + '&text=' + encodeURIComponent(
                        (options.shareText || t('inviteShareText')).replace('{url}', '').replace(/[\s:]+$/, '')
                    ),
                mode: 'ext',
                icon: '<path d="M21.9 4.3 18.7 19.4c-.2 1.1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-5 9.1-8.2c.4-.4-.1-.6-.6-.2L6.2 13.1l-4.8-1.5c-1-.3-1.1-1 .2-1.5L20.5 2.9c.9-.3 1.7.2 1.4 1.4z"/>'
            },
            {
                key : 'email',
                href: '',
                mode: 'form',
                channel: 'email',
                icon: '<path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4.2-8 5-8-5V6l8 5 8-5z"/>'
            },
            {
                key : 'sms',
                href: '',
                mode: 'form',
                channel: 'sms',
                icon: '<path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z"/>'
            }
        ];

        const buttons = targets.map(tg => `
            <button type="button" class="invite-sheet-target" data-mode="${tg.mode}" data-channel="${tg.channel || ''}" data-href="${escapeHtml(tg.href)}">
                <span class="invite-sheet-target-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">${tg.icon}</svg>
                </span>
                <span>${escapeHtml(t('inviteVia' + tg.key.charAt(0).toUpperCase() + tg.key.slice(1)))}</span>
            </button>
        `).join('');

        // System-Teilen nur anbieten, wenn es das Geraet kann -- ein toter Knopf
        // waere schlimmer als kein Knopf.
        const hasNativeShare = typeof navigator.share === 'function';

        // In welcher Sprache die Einladung rausgeht. Vorbelegt mit der eigenen,
        // aber aenderbar: Wer jemanden in Thailand einlaedt, dem nuetzt eine
        // deutsche Mail nichts. Betrifft nur E-Mail und SMS -- die gehen ueber
        // unseren Server. WhatsApp und System-Teilen nehmen den Text aus der
        // eigenen Oberflaeche, da gibt es nichts zu waehlen.
        const ownLang = VCS.state.currentLang || DEFAULT_LANG;
        const langOptions = AVAILABLE_LANGS.map(code => `
            <option value="${code}"${code === ownLang ? ' selected' : ''}>${escapeHtml(LANG_NAMES[code] || code.toUpperCase())}</option>
        `).join('');

        const overlay = document.createElement('div');
        overlay.className = 'invite-sheet-overlay';
        overlay.innerHTML = `
            <div class="invite-sheet" role="dialog" aria-modal="true">
                <div class="invite-sheet-title">${escapeHtml(options.title || t('inviteSheetTitle'))}</div>
                ${options.note ? `<p class="invite-link-hint">${escapeHtml(options.note)}</p>` : ''}
                <div class="invite-sheet-targets">${buttons}</div>

                <div class="invite-sheet-mailform" hidden>
                    <input type="email" class="invite-sheet-mailinput" inputmode="email"
                           autocomplete="email" placeholder="${escapeHtml(t('inviteMailPlaceholder'))}">
                    <label class="invite-sheet-langrow">
                        <span>${escapeHtml(t('inviteLangLabel'))}</span>
                        <select class="invite-sheet-langselect">${langOptions}</select>
                    </label>
                    <button type="button" class="invite-sheet-mailsend" data-action="send">
                        ${escapeHtml(t('inviteMailSend'))}
                    </button>
                </div>
                <div class="invite-sheet-hint" hidden></div>

                <div class="invite-sheet-link" data-action="copy" title="${escapeHtml(t('inviteCopy'))}">${escapeHtml(url)}</div>
                <div class="invite-sheet-actions">
                    <button type="button" class="invite-sheet-btn" data-action="copy">${escapeHtml(t('inviteCopy'))}</button>
                    ${hasNativeShare ? `<button type="button" class="invite-sheet-btn" data-action="native">${escapeHtml(t('inviteViaShare'))}</button>` : ''}
                    <button type="button" class="invite-sheet-btn invite-sheet-btn--close" data-action="close">${escapeHtml(t('close'))}</button>
                </div>
            </div>
        `;

        const close = () => overlay.remove();
        const form  = overlay.querySelector('.invite-sheet-mailform');
        const input = overlay.querySelector('.invite-sheet-mailinput');
        const hint  = overlay.querySelector('.invite-sheet-hint');

        // Welcher Kanal gerade aufgeklappt ist -- entscheidet, wie validiert und
        // wohin gesendet wird.
        let channel = null;

        // Live-Rueckmeldung waehrend des Tippens: erst ab der zweiten Eingabe,
        // damit einen nicht schon das erste Zeichen rot anblinkt.
        let touched = false;
        const validate = () => {
            const value = (input?.value || '').trim();
            const ok = !value || this._isValidRecipient(channel, value);
            input?.classList.toggle('is-invalid', touched && !ok);
            if (hint) {
                hint.hidden = !(touched && !ok);
                if (touched && !ok) {
                    hint.textContent = channel === 'sms' ? t('inviteSmsFormat') : t('inviteMailInvalid');
                }
            }
            return ok && !!value;
        };

        input?.addEventListener('input', () => { touched = true; validate(); });
        input?.addEventListener('blur',  () => { touched = true; validate(); });

        // Enter im Feld sendet -- sonst muesste man zum Knopf greifen.
        input?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                touched = true;
                void this._sendInvite(channel, token, input, hint, overlay);
            }
        });

        overlay.addEventListener('click', async (e) => {
            // Klick auf den Hintergrund schliesst.
            if (e.target === overlay) {
                close();
                return;
            }

            const targetBtn = e.target.closest('.invite-sheet-target');
            if (targetBtn) {
                const mode = targetBtn.getAttribute('data-mode');

                if (mode === 'form') {
                    // Eingabefeld aufklappen statt sofort zu verschicken -- Mail
                    // und SMS gehen ueber unseren Server, der die Zieladresse
                    // braucht. Feldtyp und Tastatur richten sich nach dem Kanal.
                    channel = targetBtn.getAttribute('data-channel');
                    touched = false;
                    form.hidden = false;
                    if (input) {
                        input.value = '';
                        input.classList.remove('is-invalid');
                        input.type        = channel === 'sms' ? 'tel'   : 'email';
                        input.inputMode   = channel === 'sms' ? 'tel'   : 'email';
                        input.autocomplete = channel === 'sms' ? 'tel'  : 'email';
                        input.placeholder = channel === 'sms'
                            ? t('inviteSmsPlaceholder')
                            : t('inviteMailPlaceholder');
                        input.focus();
                    }
                    if (hint) {
                        // Beim SMS-Kanal steht der Formathinweis von Anfang an da --
                        // die Laendervorwahl vergisst sonst fast jeder.
                        hint.hidden = channel !== 'sms';
                        hint.textContent = channel === 'sms' ? t('inviteSmsFormat') : '';
                    }
                    overlay.querySelectorAll('.invite-sheet-target').forEach(b => b.classList.remove('is-active'));
                    targetBtn.classList.add('is-active');
                    return;
                }

                const href = targetBtn.getAttribute('data-href');
                console.log('[invite] sending via ' + href.split(':')[0]);
                window.open(href, '_blank', 'noopener');
                close();
                return;
            }

            const action = e.target.closest('[data-action]')?.getAttribute('data-action');
            if (action === 'close') {
                close();
            } else if (action === 'send') {
                touched = true;
                await this._sendInvite(channel, token, input, hint, overlay);
            } else if (action === 'copy') {
                await this._copyInviteLink(url);
                close();
            } else if (action === 'native') {
                try {
                    // Frischer Klick -- hier greift die Web Share API zuverlaessig.
                    await navigator.share({title: options.shareTitle || t('inviteShareTitle'), text: text});
                    close();
                } catch (err) {
                    // AbortError = weggetippt. Das Sheet bleibt offen, damit der
                    // Nutzer einen anderen Weg waehlen kann.
                    if (err?.name !== 'AbortError') {
                        console.warn('[invite] native share failed:', err?.message);
                        await this._copyInviteLink(url);
                        close();
                    }
                }
            }
        });

        document.body.appendChild(overlay);
    }

    // Telefonnummer auf E.164 bringen: alles ausser Ziffern und + raus,
    // fuehrende 00 zu +. Identisch zur Normalisierung im Backend, damit der
    // Client nichts durchlaesst, was dort scheitert.
    _normalizeE164(raw) {
        let v = String(raw || '').replace(/[^+\d]/g, '');
        if (v.startsWith('00')) {
            v = '+' + v.slice(2);
        }
        return v;
    }

    // Formatpruefung vor dem Absenden.
    //
    // Bei der SMS ist das kein Komfort, sondern Kostenschutz: Eine krumme Nummer
    // faellt niemandem auf -- der Empfaenger fehlt ja nicht, er existiert nur nie
    // -- und die Nachricht ist trotzdem abgerechnet. Strikt E.164: fuehrendes +,
    // Laendercode nicht mit 0 beginnend, 8 bis 15 Ziffern.
    _isValidRecipient(channel, value) {
        if (channel === 'sms') {
            return /^\+[1-9]\d{7,14}$/.test(this._normalizeE164(value));
        }
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());
    }

    // Einladung ueber den Server verschicken -- /invite/send (Mail) oder
    // /invite/send-sms. Der Server baut den Text selbst und setzt den
    // Absendernamen aus dem Profil; von hier geht nur die Zieladresse raus.
    async _sendInvite(channel, token, input, hint, overlay) {
        const isSms = channel === 'sms';
        const raw   = (input?.value || '').trim();

        if (!raw) {
            input?.focus();
            return;
        }
        if (!token) {
            console.error('[invite] send without token');
            VCS.ui?.showToast?.(t('inviteMailFailed'), 'error', 5000);
            return;
        }

        // Formatpruefung VOR dem Request. Bei der SMS spart das im Zweifel eine
        // bezahlte Nachricht, bei der Mail einen unnoetigen Roundtrip.
        if (!this._isValidRecipient(channel, raw)) {
            input?.classList.add('is-invalid');
            if (hint) {
                hint.hidden = false;
                hint.textContent = isSms ? t('inviteSmsFormat') : t('inviteMailInvalid');
            }
            input?.focus();
            return;
        }

        const recipient = isSms ? this._normalizeE164(raw) : raw;
        // Sprache der Einladung -- nicht die eigene Oberflaechensprache, sondern
        // die im Sheet gewaehlte. Der Server prueft sie noch einmal und faellt
        // auf die Absendersprache zurueck, wenn er sie nicht kennt.
        const lang = overlay.querySelector('.invite-sheet-langselect')?.value || '';
        const btn = overlay.querySelector('.invite-sheet-mailsend');
        if (btn?.disabled) {
            return;
        }
        if (btn) {
            btn.disabled = true;
        }

        try {
            const res = await fetch(VCS.Chat.API_BASE + (isSms ? '/invite/send-sms' : '/invite/send'), {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify(isSms
                    ? {token: token, phone: recipient, lang: lang}
                    : {token: token, email: recipient, lang: lang})
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error('[invite] send failed channel=' + channel + ' status=' + res.status + ' error=' + err.error);

                if (err.error === 'invalid_phone' || err.error === 'invalid_email') {
                    input?.classList.add('is-invalid');
                    if (hint) {
                        hint.hidden = false;
                        hint.textContent = isSms ? t('inviteSmsFormat') : t('inviteMailInvalid');
                    }
                    input?.focus();
                } else if (err.error === 'sms_daily_cap') {
                    VCS.ui?.showToast?.(t('inviteSmsDailyCap').replace('{cap}', err.cap ?? ''), 'error', 6000);
                } else if (err.error === 'self_invite') {
                    VCS.ui?.showToast?.(t('inviteSmsSelf'), 'error', 5000);
                } else if (res.status === 429) {
                    VCS.ui?.showToast?.(t('inviteMailTooMany'), 'error', 6000);
                } else {
                    VCS.ui?.showToast?.(isSms ? t('inviteSmsFailed') : t('inviteMailFailed'), 'error', 5000);
                }
                return;
            }

            console.log('[invite] sent via ' + channel);
            VCS.ui?.showToast?.(
                (isSms ? t('inviteSmsSent') : t('inviteMailSent')).replace(isSms ? '{phone}' : '{email}', recipient),
                'success',
                5000
            );
            overlay.remove();
        } catch (e) {
            console.error('[invite] send error:', e);
            VCS.ui?.showToast?.(isSms ? t('inviteSmsFailed') : t('inviteMailFailed'), 'error', 5000);
        } finally {
            if (btn) {
                btn.disabled = false;
            }
        }
    }

    async _copyInviteLink(url) {
        try {
            await navigator.clipboard.writeText(url);
            VCS.ui?.showToast?.(t('inviteCopied'), 'success', 4000);
        } catch (e) {
            console.warn('[invite] clipboard failed:', e?.message);
            // Ohne Zwischenablage bleibt der Link wenigstens lesbar stehen.
            VCS.ui?.showToast?.(url, 'info', 15000);
        }
    }

    // Einen ueber /i/<token> hereingekommenen Einladungslink einloesen.
    // Aufgerufen aus signaling.js nach 'chat-authed' -- vorher gibt es keine
    // Session, gegen die der Server den Kontakt anlegen koennte.
    //
    // 'chat-authed' kommt bei JEDEM Reconnect. Das ist gewollt: bleibt ein
    // Netzwerkfehler zurueck, versucht es der naechste Verbindungsaufbau
    // erneut. Nur parallel laufen darf es nicht, daher der Lock.
    async redeemPendingInvite() {
        const token = VCS.getPendingInviteToken?.();
        if (!token || this._invitePending) {
            return;
        }
        this._invitePending = true;

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/invite/redeem', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({token: token})
            });

            const result = await res.json().catch(() => ({}));

            if (!res.ok) {
                console.warn('[invite] redeem failed status=' + res.status + ' error=' + result.error);

                // 5xx und Auth-Probleme sind voruebergehend -- Token behalten und
                // beim naechsten chat-authed neu versuchen. Alles andere ist eine
                // endgueltige Absage; ein weiterer Versuch waere nur Rauschen.
                if (res.status >= 500 || res.status === 401) {
                    return;
                }

                VCS.clearPendingInviteToken?.();
                if (result.error === 'invite_expired' || result.error === 'invite_revoked') {
                    VCS.ui?.showToast?.(t('inviteExpired'), 'error', 5000);
                } else if (result.error === 'invite_exhausted') {
                    VCS.ui?.showToast?.(t('inviteUsedUp'), 'error', 5000);
                } else if (result.error === 'self_invite') {
                    // Eigener Link auf dem eigenen Geraet -- kein Fehler, den man
                    // dem Nutzer vorhalten muesste. Still verwerfen.
                    console.log('[invite] own link opened, ignoring');
                }
                return;
            }

            VCS.clearPendingInviteToken?.();
            const peerName = result.peer?.displayName || '';
            console.log('[invite] redeemed status=' + result.status + ' chatId=' + result.chatId);

            // Bei 'already_contacts' waren die beiden schon verbunden -- dann ist
            // die Erfolgsmeldung unpassend, der Chat wird trotzdem geoeffnet.
            if (result.status === 'redeemed') {
                VCS.ui?.showToast?.(t('inviteConnected').replace('{name}', peerName), 'success', 5000);
            }

            if (result.peer?.phoneNumber) {
                this.saveContact(result.peer.phoneNumber, peerName || result.peer.phoneNumber);
            }
            await this.loadContacts?.();
            await VCS.chatSetup?.loadChats?.();
            if (result.chatId) {
                VCS.chatSetup?.openConversation?.(result.chatId);
            }
        } catch (e) {
            // Netzwerkabbruch: Token bleibt liegen, naechster Reconnect probiert es.
            console.error('[invite] redeem error:', e);
        } finally {
            this._invitePending = false;
        }
    }

    showContactContextMenu(x, y, phone, name) {
        // Vorhandenes Menue zuerst entfernen
        document.querySelector('.contact-ctx-menu')?.remove();

        const menu = document.createElement('div');
        menu.className = 'chat-context-menu contact-ctx-menu';
        menu.style.position = 'fixed';
        menu.style.zIndex = '10000';
        menu.style.visibility = 'hidden';
        // data-action="delete" -> nutzt rote Farbe aus .chat-context-menu button[data-action="delete"]
        menu.innerHTML = `
            <button type="button" data-action="delete">
                <svg class="chat-ctx-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path></svg>
                <span>${escapeHtml(t('contactRemove'))}</span>
            </button>
        `;

        document.body.appendChild(menu);

        // Position klemmen, damit Menu nicht aus dem Viewport ragt
        const rect = menu.getBoundingClientRect();
        const pad = 8;
        const left = Math.min(x, window.innerWidth - rect.width - pad);
        const top  = Math.min(y, window.innerHeight - rect.height - pad);
        menu.style.left = Math.max(pad, left) + 'px';
        menu.style.top  = Math.max(pad, top) + 'px';
        menu.style.visibility = 'visible';

        const close = () => {
            menu.remove();
            document.removeEventListener('click', onOutside, true);
            document.removeEventListener('contextmenu', onOutside, true);
            document.removeEventListener('keydown', onKey, true);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close, true);
        };
        const onOutside = (e) => { if (!menu.contains(e.target)) close(); };
        const onKey = (e) => { if (e.key === 'Escape') close(); };

        menu.querySelector('[data-action]').addEventListener('click', (e) => {
            e.stopPropagation();
            close();
            this.removeContact(phone, name);
        });

        // Async anhaengen, damit der ausloesende Click/Contextmenu nicht sofort schliesst
        setTimeout(() => {
            document.addEventListener('click', onOutside, true);
            document.addEventListener('contextmenu', onOutside, true);
            document.addEventListener('keydown', onKey, true);
            window.addEventListener('scroll', close, true);
            window.addEventListener('resize', close, true);
        }, 0);
    }

    async removeContact(phone, name) {
        if (!phone) return;
        const trashIcon = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path></svg>';
        const confirmMsg = t('confirmContactRemove').replace('{0}', name || phone);
        const ok = await VCS.ui?.showConfirm?.(confirmMsg, trashIcon, t('contactRemove'));
        if (!ok) return;

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/contacts/remove', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({ phone })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error('[chat] removeContact failed:', res.status, err);
                VCS.ui?.showToast?.('Error', 'error');
                return;
            }
            const data = await res.json().catch(() => ({}));
            console.log('[chat] contact removed:', phone, 'rows:', data.removed);

            // Lokal aufraeumen
            VCS.state.chatApprovedContacts.delete(phone);
            VCS.state.chatSavedContacts = VCS.state.chatSavedContacts.filter(c => c.phoneNumber !== phone);
            localStorage.setItem('chat-contacts', JSON.stringify(VCS.state.chatSavedContacts));

            // Falls aktuell offener Chat = Chat zum entfernten Peer, schliessen
            const activeId = VCS.state.chatActiveChatId;
            if (activeId) {
                const activeChat = VCS.state.chatChats.find(c => c.id === activeId);
                const activePeer = activeChat ? VCS.chat?.peerPhone?.(activeChat) : null;
                const isGroup = activeChat?._isGroup || activeChat?.isGroup;
                if (activePeer === phone && !isGroup) {
                    VCS.state.chatActiveChatId = null;
                    VCS.signaling?.sendActiveChat?.();
                    VCS.state.chatActiveIsGroup = false;
                    VCS.state.chatEditingMessageId = null;
                    VCS.chat?.clearReply?.();
                    VCS.nav?.switchTab?.('tabChats');
                }
            }

            VCS.ui?.showToast?.(t('contactRemoved'), 'success');
            VCS.chatMessages?.renderChatList?.();
            this.loadContacts();
        } catch (e) {
            console.error('[chat] removeContact error:', e);
            VCS.ui?.showToast?.('Network error', 'error');
        }
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        $('#btnStartNewChat')[0]?.addEventListener('click', () => this.startNewChat());
    }

    // ── Private Methoden ─────────────────────────────────────

    updateContactRequestBadge() {
        const badge = $('#navContactsBadge')[0];
        if (!badge) {
            return;
        }
        const count = VCS.state.chatPendingRequests.length;
        if (count > 0) {
            badge.textContent = count;
            badge.classList.remove('hidden');
        } else {
            badge.classList.add('hidden');
        }
    }
};
